import React from "react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const LAST_UPDATED = "February 10, 2026";

const Privacy = () => {
  useDocumentMeta({
    title: "Privacy Policy",
    description:
      "How ProsoftMarketplace collects, uses, and protects your data. Independent reseller of Huntheal products.",
  });

  return (
    <div data-testid="privacy-page" className="bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Legal</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 text-slate-700 sm:px-6 lg:px-8">
        <Block title="1. Who we are">
          ProsoftMarketplace is an independent reseller of antivirus software (Huntheal
          and others) operated from 2118 Glenridge Ct, Marietta, GA 30062, USA. You can contact us at{" "}
          <a className="text-emerald-600 underline" href="mailto:support@prosoftmarketplace.com">
            support@prosoftmarketplace.com
          </a>{" "}
          or +1 (888) 655-4464.
        </Block>

        <Block title="2. Information we collect">
          We collect only what we need to complete your order and provide support:
          <List
            items={[
              "Contact info you submit (name, email, phone, billing address).",
              "Order data (products purchased, plan selected, order reference).",
              "Payment metadata from PayPal (transaction ID, status). We never receive or store full card details.",
              "Optional phone numbers submitted to our SMS opt-in popup.",
              "Standard server logs (IP address, browser, pages viewed) for security and analytics.",
            ]}
          />
        </Block>

        <Block title="3. How we use your information">
          <List
            items={[
              "To deliver activation keys, order confirmations, and renewal reminders.",
              "To respond to support requests.",
              "To send occasional promotional offers (you can unsubscribe any time).",
              "To detect fraud and protect the integrity of our store.",
            ]}
          />
        </Block>

        <Block title="4. Who we share data with">
          <List
            items={[
              "PayPal — to process payments. PayPal's privacy policy applies to data they collect.",
              "Antivirus vendors (Huntheal) — to assign your activation key when needed.",
              "Email and SMS providers — to deliver order confirmations and promotional messages.",
            ]}
          />
          We never sell your personal information.
        </Block>

        <Block title="5. Your choices">
          <List
            items={[
              "Email us at support@prosoftmarketplace.com to request access, correction, or deletion of your data.",
              "Reply STOP to any SMS to unsubscribe immediately.",
              "Use the unsubscribe link in any promotional email.",
            ]}
          />
          We honor verified requests within 30 days, in line with GDPR and CCPA.
        </Block>

        <Block title="6. Cookies">
          We use first-party cookies and localStorage to remember your cart and your opt-in preferences.
          We do not use third-party advertising trackers. Analytics, if any, use anonymized aggregated data only.
        </Block>

        <Block title="7. Security">
          All checkout is processed by PayPal over bank-grade 256-bit TLS. Order data is stored in a private,
          encrypted database. We follow industry best practices, but no system is 100% secure — please use a
          strong, unique password if you create an account.
        </Block>

        <Block title="8. Children">
          ProsoftMarketplace is intended for adults aged 18 and older. We do not knowingly collect data from minors.
        </Block>

        <Block title="9. Trademark notice">
          Huntheal mentioned on this site are trademarks of their
          respective owners. We are an independent reseller and are not affiliated with, endorsed by, or partnered
          with any of these brands unless explicitly stated.
        </Block>

        <Block title="10. Changes to this policy">
          We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date at the top of this page
          reflects when the most recent change was made. Continued use of our website constitutes acceptance.
        </Block>

        <div className="mt-12 border border-slate-200 bg-white p-6 text-sm">
          Questions? Reach us at{" "}
          <a className="font-bold text-emerald-600 underline" href="mailto:support@prosoftmarketplace.com">
            support@prosoftmarketplace.com
          </a>{" "}
          or call <a className="font-bold text-emerald-600 underline" href="tel:+18886554464">+1 (888) 655-4464</a>.
        </div>
      </section>
    </div>
  );
};

const Block = ({ title, children }) => (
  <section className="mt-8 first:mt-0">
    <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
    <div className="mt-3 text-base leading-relaxed text-slate-700">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
    {items.map((it) => (
      <li key={it}>{it}</li>
    ))}
  </ul>
);

export default Privacy;
